import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { PasswordField } from "../components/PasswordField";
import { useAuth } from "../auth/AuthContext";
import { useProductBranding } from "../lib/branding";
import { homePathFor } from "../nav/navigation";

/** Куда вернуться после входа, если пользователя перенаправили на /login с защищённого пути. */
interface LocationState {
  from?: { pathname?: string };
}

/**
 * Страница входа (SK-5). Отправляет логин/пароль на POST /api/auth/login и разбирает
 * состояния SK-4: неверные данные (401), лимит попыток (429), ошибка валидации (400).
 * Скорость оператора (docs/07 §2): фокус в первом поле, Enter — отправка.
 */
export function LoginPage() {
  const { status, user, login } = useAuth();
  const branding = useProductBranding();
  const location = useLocation();

  const [loginValue, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as LocationState | null)?.from?.pathname;

  // Уже вошёл — на страницу входа не пускаем, возвращаем на исходный/стартовый раздел.
  if (status === "authenticated" && user) {
    return <Navigate to={from ?? homePathFor(user.roles)} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    const result = await login(loginValue.trim(), password);
    if (result.ok) {
      // Пароль в состоянии не задерживаем; редирект на стартовый раздел выполнит guard
      // выше по смене статуса на "authenticated" (единый механизм, без двойной навигации).
      setPassword("");
      return;
    }

    setSubmitting(false);
    setError(
      result.retryAfterSec
        ? `${result.message} Повторите через ${result.retryAfterSec} с.`
        : result.message,
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      {/* Карточка шире прежних 380px: дескриптор (56 символов) иначе расползался на три
          строки. С D-13 он набран прописными с разрядкой и занимает две строки осознанно —
          дальше карточку не расширяем, титул не должен быть шире формы входа. */}
      <div className="w-full max-w-[var(--w-form-narrow)]">
        {/* Брендинг инстанса (D-10): имя продукта из конфигурации и постоянная расшифровка.
            Пока ответ сервера не пришёл (или api недоступен) — имени нет, но форма входа
            работает: подставить своё имя нельзя, у инстанса оно может быть другим. */}
        {/* Полировка титульного блока (D-13, решение владельца 05.08.2026): имя крупнее и
            весомее, дескриптор — приглушённой строкой прописными с разрядкой, над именем —
            короткая акцентная черта. Размеры В ШАПКЕ приложения не меняются: там брендинг
            служебный, здесь — титульный, это разные задачи одной строки. */}
        <div className="mb-5 flex flex-col items-center text-center">
          {/* Фирменный акцент: короткая черта акцентного цвета над именем. Цвет — токен
              --color-accent (docs/07), новых цветов не заводим. Декор, поэтому скрыт от
              озвучивания: экранный диктор не должен читать полоску как содержимое. */}
          <span
            aria-hidden="true"
            className="mb-3 h-[3px] w-10 rounded-[var(--radius-s)] bg-accent"
          />
          {/* Заголовок первого уровня есть всегда: без брендинга это «Вход в систему» —
              структура заголовков страницы не должна ломаться из-за недоступного api.
              Прописные буквы даёт стиль, а не сама строка: в конфигурации и в ответе api
              имя лежит как написано («Облигат»), капс — оформление титула (docs/07, Д-1). */}
          <h1 className="doc-title text-[34px] font-bold uppercase leading-tight tracking-[0.02em] text-ink">
            {branding?.name ?? "Вход в систему"}
          </h1>
          {/* Дескриптор — вспомогательная строка, а не второй заголовок: мелкий кегль,
              прописные с разрядкой 0.1em и приглушённый цвет (--color-muted). Разрядка
              обязательна — прописные без неё читаются как сплошная полоса.
              В ОДНУ строку он теперь не помещается и не должен: прописные с разрядкой шире
              строчных примерно в полтора раза, а 56 символов при кегле 11,5px требуют около
              490px против 440px карточки. Поэтому перенос разрешён, а `text-balance` делит
              строки поровну — иначе вторая строка вышла бы коротким хвостом. */}
          {branding && (
            <p className="mt-2 text-balance text-[11.5px] uppercase leading-snug tracking-[0.1em] text-muted">
              {branding.descriptor}
            </p>
          )}
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-[var(--radius-l)] border border-border bg-surface p-6"
          aria-labelledby="login-heading"
        >
          <h2 id="login-heading" className="sr-only">
            Форма входа
          </h2>

          <label htmlFor="login" className="block text-[13px] font-bold text-ink">
            Логин
          </label>
          <input
            id="login"
            name="login"
            type="text"
            autoComplete="username"
            autoFocus
            required
            value={loginValue}
            onChange={(e) => setLoginValue(e.target.value)}
            disabled={submitting}
            className="mt-1 mb-4 w-full rounded-[var(--radius-m)] border border-border bg-surface px-3 py-2 text-[15px] text-ink disabled:opacity-60"
          />

          {/* Поле с «глазом» — общий компонент (D-1); вторая его форма появилась в D-15. */}
          <PasswordField
            id="password"
            label="Пароль"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            disabled={submitting}
          />

          {error && (
            <p
              role="alert"
              aria-live="assertive"
              className="mt-4 rounded-[var(--radius-m)] bg-overdue-bg px-3 py-2 text-[13px] text-overdue-fg"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-5 w-full rounded-[var(--radius-m)] bg-accent px-3 py-2 text-[15px] font-bold text-surface disabled:opacity-60"
          >
            {submitting ? "Вход…" : "Войти"}
          </button>
        </form>
      </div>
    </div>
  );
}
